import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useRef,
} from "react";

import styled, { ThemeProvider } from "styled-components";
import { navigate } from "gatsby";
import { Helmet } from "react-helmet";
import AOS from "aos";

import TopBar from "../TopBar";
import Header from "../Header";
import Footer from "../Footer";

import SidebarDashboard from "../SidebarDashboard";
import ModalVideo from "../ModalVideo";
import ModalApplication from "../ModalApplication";
import ModalSignIn from "../ModalSignIn";
import ModalSignUp from "../ModalSignUp";
import ModalAuth from "../ModalAuth";
// import ModalPostLoginPlan from "../ModalPostLoginPlan";
import ActivationFormMain from "../ActivationForm";

import GlobalContext from "../../context/GlobalContext";
// import { fetchUserActivePlanDetails } from "../../services/auth";
import { fetchDashboardUserStatus } from "../../services/tenders";
import { useAuth } from "../../context/AuthContext";

import GlobalStyle from "../../utils/globalStyle";
import TrailingSlashHandler from "../TrailingSlashHandler/TrailingSlashHandler";
import { normalizePathWithTrailingSlash } from "../../utils/url";

import "../../assets/fonts/fontawesome-5/webfonts/fa-brands-400.ttf";
import "../../assets/fonts/fontawesome-5/webfonts/fa-regular-400.ttf";
import "../../assets/fonts/fontawesome-5/webfonts/fa-solid-900.ttf";

import "../../assets/fonts/icon-font/fonts/avasta.ttf";
import "../../assets/fonts/icon-font/css/style.css";

import "../../../node_modules/slick-carousel/slick/slick.css";
import "../../../node_modules/slick-carousel/slick/slick-theme.css";
import "../../../node_modules/aos/dist/aos.css";

import "../../assets/fonts/icon-font/css/style.css";
import "../../assets/fonts/fontawesome-5/css/all.css";

import "../../scss/bootstrap.scss";
import "../../scss/main.scss";

import { get, merge } from "lodash";

// the full theme object
import { theme as baseTheme } from "../../utils";

const Loader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #fff;
  z-index: 9999999999;
  opacity: 1;
  visibility: visible;
  transition: all 1s ease-out 0.5s;
  &.inActive {
    opacity: 0;
    visibility: hidden;
  }
`;

// options for different color modes
const SITE_ORIGIN = "https://www.tenderapex.com";

const getCanonicalHref = (location) => {
  if (typeof window !== "undefined" && window.location && window.location.href) {
    return window.location.href;
  }

  const canonicalPath = normalizePathWithTrailingSlash((location && location.pathname) || "/");
  const search = (location && location.search) || "";
  const hash = (location && location.hash) || "";

  return `${SITE_ORIGIN}${canonicalPath}${search}${hash}`;
};
const modes = { light: "light", dark: "dark" };

// merge the color mode with the base theme
// to create a new theme object
const getTheme = (mode) =>
  merge({}, baseTheme, {
    colors: get(baseTheme.colors.modes, mode, baseTheme.colors),
  });

const Layout = ({ children, pageContext, location }) => {
  const gContext = useContext(GlobalContext);
  const { isAuthenticated, isLoading, user } = useAuth();
  const isDashboardSettings = location?.pathname === "/dashboard-settings";
  const dashboardPathname =
    (location?.pathname || "").replace(/\/$/, "") || "/";
  /** Allow guests to open Tender 360 Bidder Profile (blur/restrictions handled in-page). */
  const allowGuestDashboardBidderProfile =
    dashboardPathname === "/dashboard-bidder-profile";

  const [visibleLoader, setVisibleLoader] = useState(true);
  const userId = user?.id ?? user?.uid ?? user?.user_id ?? null;
  const userPlans = user?.plans;
  const hasPlans = Array.isArray(userPlans) && userPlans.length > 0;
  const canonicalHref = getCanonicalHref(location);

  useLayoutEffect(() => {
    AOS.init({ once: true });
    setVisibleLoader(false);
  }, []);

  // Navbar style based on scroll
  const eleRef = useRef();

  useEffect(() => {
    window.addEventListener(
      "popstate",
      function(event) {
        // The popstate event is fired each time when the current history entry changes.
        gContext.closeOffCanvas();
      },
      false
    );
    window.addEventListener(
      "pushState",
      function(event) {
        // The pushstate event is fired each time when the current history entry changes.
        gContext.closeOffCanvas();
      },
      false
    );
  }, [gContext]);

  // Protected dashboard: redirect unauthenticated users to home and show auth modal
  useEffect(() => {
    if (pageContext?.layout !== "dashboard" || isLoading) return;
    if (!isAuthenticated && !allowGuestDashboardBidderProfile) {
      gContext.toggleAuthModal();
      navigate("/", { replace: true });
    }
  }, [
    pageContext?.layout,
    isAuthenticated,
    isLoading,
    gContext,
    allowGuestDashboardBidderProfile,
  ]);


  useEffect(() => {
    if (pageContext?.layout !== "dashboard" || isLoading || !isAuthenticated) return;
    if (hasPlans || !userId) return;
    let cancelled = false;
    fetchDashboardUserStatus(userId)
      .then((status) => {
        if (cancelled) return;
        if (status?.status === "no_payment") {
          navigate("/pricing", { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) navigate("/pricing", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [pageContext?.layout, isAuthenticated, isLoading, hasPlans, userId]);
  // Post-login Freemium plan popup hidden.
  // const userId = user?.id ?? user?.uid ?? user?.user_id ?? null;
  // useEffect(() => {
  //   if (!isAuthenticated || !userId || typeof window === "undefined") return;
  //   const storageKey = "post_login_plan_popup_checked_v2";
  //   if (sessionStorage.getItem(storageKey)) return;
  //   let isMounted = true;
  //   Promise.all([
  //     fetchUserActivePlanDetails(userId).catch(() => ({})),
  //     fetchUserPlanIdForActivationForm(userId),
  //   ])
  //     .then(([activeData, activationRes]) => {
  //       if (!isMounted) return;
  //       sessionStorage.setItem(storageKey, "1");
  //       const activePlans = Array.isArray(activeData) ? activeData : (activeData?.plans ?? []);
  //       const activationPlans = Array.isArray(activationRes?.user_plans) ? activationRes.user_plans : [];
  //       const hasActivePlans = activePlans.length > 0;
  //       const hasActivationPlans = activationPlans.length > 0;
  //       if (!hasActivePlans && !hasActivationPlans) {
  //         gContext.setPostLoginPlanPopupVisible(true);
  //       }
  //     })
  //     .catch(() => {
  //       if (isMounted) sessionStorage.setItem(storageKey, "1");
  //     });
  //   return () => { isMounted = false; };
  // }, [isAuthenticated, userId, gContext]);

  // Tawk.to chat widget – load on all pages except Dashboard and Pricing; hide when on those pages
  /*
  const isDashboard = pageContext?.layout === "dashboard";
  const pathname = (location?.pathname || "").replace(/\/$/, "") || "/";
  const isPricingPage = pathname === "/pricing";
  const showTawk = !isDashboard && !isPricingPage;
  useEffect(() => {
    if (!showTawk || typeof window === "undefined" || window.Tawk_API) return;
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    const s1 = document.createElement("script");
    const s0 = document.getElementsByTagName("script")[0];
    s1.async = true;
    s1.src = "https://embed.tawk.to/69a0488d1f874e1c38ab8a28/1jid1mqo2";
    s1.charset = "UTF-8";
    s1.setAttribute("crossorigin", "*");
    s0.parentNode.insertBefore(s1, s0);
  }, [showTawk]);
  useEffect(() => {
    if (typeof window === "undefined" || !window.Tawk_API) return;
    if (showTawk) {
      window.Tawk_API.showWidget?.();
    } else {
      window.Tawk_API.hideWidget?.();
    }
  }, [showTawk, location?.pathname, pageContext?.layout]);
  */

  if (pageContext.layout === "bare") {
    return (
      <ThemeProvider
        theme={
          gContext.themeDark ? getTheme(modes.dark) : getTheme(modes.light)
        }
      >
        <div data-theme-mode-panel-active data-theme="light">
          <TrailingSlashHandler location={location} />
          <GlobalStyle />
          <Helmet>
            <title>Tender Apex</title>
            <link rel="canonical" href={canonicalHref} />
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link href="https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600;700;800;900&display=swap" rel="stylesheet" />
          </Helmet>
          <Loader id="loading" className={visibleLoader ? "" : "inActive"}>
            <div className="load-circle">
              <span className="one"></span>
            </div>
          </Loader>
          <div className="site-wrapper overflow-hidden position-relative min-h-100vh" ref={eleRef}>
            {children}
                    <ActivationFormMain />
          </div>

          <ModalVideo />
          <ModalApplication />
          <ModalSignIn />
          <ModalSignUp />
          <ModalAuth />
          {/* <ModalPostLoginPlan /> */}
        </div>
      </ThemeProvider>
    );
  }

  if (pageContext.layout === "dashboard") {

    if (isLoading) {
      return (
        <ThemeProvider
          theme={
            gContext.themeDark ? getTheme(modes.dark) : getTheme(modes.light)
          }
        >
          <div data-theme-mode-panel-active data-theme="light">
            <TrailingSlashHandler location={location} />
            <GlobalStyle />
            <Helmet>
              <title>Tender Apex</title>
              <link rel="canonical" href={canonicalHref} />
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
              <link href="https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600;700;800;900&display=swap" rel="stylesheet" />
            </Helmet>
            <Loader id="loading" className="">
              <div className="load-circle">
                <span className="one"></span>
              </div>
            </Loader>
          </div>
        </ThemeProvider>
      );
    }

    if (!isAuthenticated && !allowGuestDashboardBidderProfile) {
      return null;
    }

    if (isAuthenticated && !hasPlans) {
      return (
        <ThemeProvider
          theme={
            gContext.themeDark ? getTheme(modes.dark) : getTheme(modes.light)
          }
        >
          <div data-theme-mode-panel-active data-theme="light">
            <TrailingSlashHandler location={location} />
            <GlobalStyle />
            <Helmet>
              <title>Tender Apex</title>
              <link rel="canonical" href={canonicalHref} />
            </Helmet>
            <Loader id="loading" className="">
              <div className="load-circle">
                <span className="one"></span>
              </div>
            </Loader>
          </div>
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider
        theme={
          gContext.themeDark ? getTheme(modes.dark) : getTheme(modes.light)
        }
      >
        <div data-theme-mode-panel-active data-theme="light">
          <TrailingSlashHandler location={location} />
          <GlobalStyle />
          <Helmet>
            <title>Tender Apex</title>
            <link rel="canonical" href={canonicalHref} />
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link href="https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600;700;800;900&display=swap" rel="stylesheet" />
          </Helmet>
          <Loader id="loading" className={visibleLoader ? "" : "inActive"}>
            <div className="load-circle">
              <span className="one"></span>
            </div>
          </Loader>
          <style>{`@media print { .print-hide-logout { display: none !important; } .dashboard-print-hide-filters { display: none !important; } }`}</style>
          <div
            className="site-wrapper overflow-hidden bg-default-2 position-relative min-h-100vh"
            ref={eleRef}
          >
            <TopBar />
            <Header isDark={gContext.headerDark} />
            {!isDashboardSettings && <SidebarDashboard />}
            {children}
            <ActivationFormMain />
          </div>

          <ModalVideo />
          <ModalApplication />
          <ModalSignIn />
          <ModalSignUp />
          <ModalAuth />
          {/* <ModalPostLoginPlan /> */}
        </div>
      </ThemeProvider>
    );
  }

  return (
    <>
      <ThemeProvider
        theme={
          gContext.themeDark ? getTheme(modes.dark) : getTheme(modes.light)
        }
      >
        <div data-theme-mode-panel-active data-theme="light">
          <TrailingSlashHandler location={location} />
          <GlobalStyle />
          <Helmet>
            <title>Tender Apex</title>
            <link rel="canonical" href={canonicalHref} />
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link href="https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;600;700;800;900&display=swap" rel="stylesheet" />
          </Helmet>
          <Loader id="loading" className={visibleLoader ? "" : "inActive"} />
          <style>{`@media print { .print-hide-logout { display: none !important; } .dashboard-print-hide-filters { display: none !important; } }`}</style>
          <style>{`
            .site-wrapper-with-topbar { padding-top: 44px !important; }
            @media (min-width: 768px) and (max-width: 991px) {
              .site-wrapper-with-topbar { padding-top: 40px !important; }
            }
            @media (max-width: 767px) {
              .site-wrapper-with-topbar { padding-top: 38px !important; }
            }
          `}</style>
          <div className="site-wrapper site-wrapper-with-topbar overflow-hidden position-relative min-h-100vh" ref={eleRef}>
            <TopBar />
            <Header isDark={gContext.headerDark} publicPage />
            {children}
            <ActivationFormMain />

            {/* Disclaimer Section */}
            <div
              className="border-top py-3 py-md-4 py-lg-5 disclaimer-section"
              style={{
                background: "#eceef1",
                borderTopWidth: "2px",
                borderTopStyle: "solid",
                borderTopColor: "rgba(0, 176, 116, 0.3)",
              }}
            >
              <div className="container px-4 px-md-5 px-lg-6">
                <p className="text-center mb-0 font-size-3 line-height-1p6 text-black-2">
                  <i className="fas fa-info-circle mr-2 text-black-2" aria-hidden="true" />
                  <span className="font-weight-semibold text-black-2">Disclaimer:</span>
                  {" "}All the insights on Tender Apex are provided for convenience. Users are advised to always verify with the official tender document before making any bidding decisions.
                </p>
              </div>
            </div>

            <Footer isDark={gContext.footerDark} />
          </div>

          <ModalVideo />
          <ModalApplication />
          <ModalSignIn />
          <ModalSignUp />
          <ModalAuth />
          {/* <ModalPostLoginPlan /> */}
        </div>
      </ThemeProvider>
    </>
  );
};

export default Layout;
